import { CheckoutRuleDocumentType } from '../interfaces'
import type { SessionCheckoutRule } from '../graphQL/queries/getSession/types'
import type { RootState } from '../redux/store'

/**
 * Has the shopper chosen a value for this checkout rule?
 *
 * One module, because two subsystems need the same answer and must not drift:
 * `utils/cartCheckoutPanel.ts` renders the pick on the cart's Entrega/Pago
 * rows, and COM-1046's summary CTA decides between *Continuar* and *Realizar
 * pedido* on it. COM-1048 reads it again after auto-settling a one-option rule.
 *
 * **Opening a picker is not choosing** (Product, 2026-09-23). A shopper who
 * opens the calendar and backs out has settled nothing, and the CTA keeps them
 * there. So nothing here consults a screen visit, and there is no `visited` map
 * to consult.
 *
 * Two places hold a pick and this module reads both:
 *
 * - `checkoutSlice.selections[ruleType]` — this session's pick, written by the
 *   picker screens through `useSetCheckoutRuleSelection` and **only** on the
 *   mutation's success. Lost on reload.
 * - `sessionData.checkoutRules` — the persisted pick the boot response carries.
 *   Survives reload; goes stale the moment a write lands, because nothing
 *   updates it in place.
 *
 * The fresh pick therefore wins and the persisted one backs it. That ordering
 * is what makes a reload safe *and* keeps the cart row from showing yesterday's
 * date for the rest of the session.
 */

/**
 * The persisted pick for one rule type, or `undefined`.
 *
 * The single reader of `session.checkoutRules`. `utils/cartCheckoutPanel.ts`
 * called a private `findData` for this until COM-1045; it now calls here, so
 * the panel and the settled-selector cannot disagree about what the array says.
 *
 * ⚠️ **An entry is not a pick.** `value` is a nullable
 * `SessionCheckoutRuleValue` and its `data` is `string | null | undefined`, so
 * a rule the storefront wrote with nothing chosen arrives as
 * `{ ruleType, value: null }` or `{ ruleType, value: { data: null } }`. Read
 * the chain as `?.value?.data`; `.value.data` throws on the first of those.
 *
 * `setCheckoutRuleById` filters and appends server-side, so the array holds at
 * most one entry per `ruleType`. `find` reflects that. A second entry for the
 * same type is ignored rather than rejected — cinnamon has no authority to
 * refuse a shape the server accepted, and picking between two would be a coin
 * toss.
 *
 * `ruleType` on the wire is `String!` with no enum behind it, so the array can
 * carry a type this build has never heard of. Comparing by value is safe
 * because `SessionRuleTypeMatchesConfig` in `getSession/types.ts` fails the
 * build if `SessionCheckoutRuleType` and `CheckoutRules` ever drift apart.
 */
export const readSessionRulePick = (
  rules: SessionCheckoutRule[] | null | undefined,
  ruleType: CheckoutRuleDocumentType,
): string | undefined => {
  if (!Array.isArray(rules) || rules.length === 0) return undefined

  const match = rules.find((rule) => rule?.ruleType === ruleType)
  const data = match?.value?.data

  return typeof data === 'string' && data !== '' ? data : undefined
}

/**
 * The shopper's pick for one rule type: this session's first, the persisted one
 * behind it.
 *
 * **The precedence is load-bearing in both directions.** Reading the session
 * copy first would show a stale value after every write, because
 * `sessionData` is boot-time data that `setCheckoutRuleSession` does not
 * update in place (COM-1066 research, 2026-09-28). Reading only the fresh copy
 * would walk a returning shopper through pickers they have already answered.
 *
 * Both sides are normalised to "a non-empty string, or nothing". The two types
 * disagree — `ICheckoutRuleValue.data` is a required `string` while the
 * session's is `string | null | undefined` — and without the normalisation an
 * empty string would read as settled on one side and unsettled on the other
 * for the same shopper.
 */
export const selectRulePick = (
  state: RootState,
  ruleType: CheckoutRuleDocumentType,
): string | undefined => {
  /*
   * Optional all the way down, matching `isHidePricesEnabled` beside it.
   * `checkoutSlice` is registered on the real store and cannot be absent at
   * runtime, but a `RootState` in this repo is routinely a partial object cast
   * into shape by a test fixture, and a selector that throws on one is a
   * selector nobody can call from a suite that does not care about checkout.
   */
  const fresh = state?.checkoutSlice?.selections?.[ruleType]?.data
  if (typeof fresh === 'string' && fresh !== '') return fresh

  return readSessionRulePick(
    state?.defaultSlice?.sessionData?.checkoutRules,
    ruleType,
  )
}

/**
 * Whether the shopper has chosen a value for this rule.
 *
 * The one thing COM-1046's guided flow and COM-1048's auto-settle ask. Curried
 * so it reads as `useAppSelector(selectRuleIsSettled(ruleType))`, matching
 * `selectSelectionByRuleType` and `selectAvailableByRuleType` in
 * `redux/slices/checkout/checkout.ts`.
 *
 * It lives in `utils/` rather than beside those two because it spans slices —
 * the same reason `selectOrderDistributor` and `isHidePricesEnabled` do.
 *
 * ⚠️ **Settled says nothing about whether the rule was changeable.** A rule
 * with one option is settled by COM-1048 writing it, and a rule with no
 * `checkoutRuleRoutes` entry is settled by whatever the storefront persisted.
 * Ask `resolveRuleChangeRoute` for changeability; asking this instead strands
 * a shopper on a CTA that goes nowhere.
 */
export const selectRuleIsSettled =
  (ruleType: CheckoutRuleDocumentType) =>
  (state: RootState): boolean =>
    selectRulePick(state, ruleType) !== undefined

export default selectRuleIsSettled
